import { Avatar, Divider, Grid, Typography } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { HttpCacheWorkflow } from 'src/http/workflow/httpCacheWorkflow';
import { EntityWithIdAndDescriptionFields, EntityWithIdFields, SectorComercialFields, UnidadNegocioFields } from 'src/types';
import { SistemaPrincipalFields } from "src/types/workflow/cache";
import { AsyncSelect, ControlledDatePicker, ControlledTextField } from "../../../../../components/forms";
import { useLoadOptions } from "../../../../../hooks/useLoadOptions";
import CardActions from '../../../components/CardActions';
import { SectionBox } from '../../../components/FieldView';
import { useADCMClienteTipos, useADCMEmisorTipos, useADCMSectorComercialTipos, useADCMTransaccionTipos, useADCMUnidadNegocio } from "../hooks/useADCMCache";
import { useSiNoOptions } from '../hooks/useOpcionesGenericas';
import { ADCMClienteTipo } from '../types/enum';
import { OperacionADCMFields } from '../types/fields';
import { UnidadNegocioTipo, SectorComercialTipo } from 'src/types/enum/enum';

interface SeccionADCMDatosOperacionProps {
  formMethods: UseFormReturn<any>
  codSistema: number,
  codPais: number
}

export const SeccionNuevoADCMDatosOperacion = ({ formMethods, codSistema, codPais }: SeccionADCMDatosOperacionProps) => {
  const { control, setValue } = formMethods
  const unidadNegocioHook = useADCMUnidadNegocio(codPais, codSistema)

  const loadSiNo = useSiNoOptions();

  const loadUnidadNegocio = useLoadOptions({
    data: unidadNegocioHook.unidadNegocio,
    loading: unidadNegocioHook.isLoading,
    isError: unidadNegocioHook.isError,
    error: unidadNegocioHook.error
  });

  const watchedUnidadNegocio = useWatch({ control: control, name: OperacionADCMFields.CodUnidadNegocio });

  const sectorComercialHook = useADCMSectorComercialTipos(watchedUnidadNegocio, codSistema);
  const loadSectorComercial = useLoadOptions({
    data: sectorComercialHook.sectorComercialTipos,
    loading: sectorComercialHook.isLoading,
    isError: sectorComercialHook.isError,
    error: sectorComercialHook.error
  });

  useEffect(() => {
    if (unidadNegocioHook.unidadNegocio?.length) {
      const match = unidadNegocioHook.unidadNegocio.find(
        (un) => un[UnidadNegocioFields.UnidadNegocioTipo] === UnidadNegocioTipo.CorporateAndInvestmentBanking
      );
      if (match) {
        setValue(OperacionADCMFields.CodUnidadNegocio, match[SectorComercialFields.UnidadNegocioCodigo]);
      }
    }
  }, [unidadNegocioHook.unidadNegocio, setValue]);


  useEffect(() => {
    if (sectorComercialHook.sectorComercialTipos?.length) {
      const match = sectorComercialHook.sectorComercialTipos.find(
        (sc) => sc[SectorComercialFields.SectorComercialTipo] === SectorComercialTipo.CapitalMarkets
      );
      if (match) {
        setValue(OperacionADCMFields.CodSectorComercial, match[SectorComercialFields.SectorComercialCodigo]);
      }
    }
  }, [sectorComercialHook.sectorComercialTipos, setValue]);

  const clienteTipos = useADCMClienteTipos(codPais);
  const loadCliente = useLoadOptions({
    data: clienteTipos.clienteTipos,
    loading: clienteTipos.isLoading,
    isError: clienteTipos.isError,
    error: clienteTipos.error
  });

  const emisorTipos = useADCMEmisorTipos(codPais);
  const loadEmisor = useLoadOptions({
    data: emisorTipos.emisorTipos,
    loading: emisorTipos.isLoading,
    isError: emisorTipos.isError,
    error: emisorTipos.error
  });

  const transaccionTipos = useADCMTransaccionTipos(codPais);
  const loadTransaccion = useLoadOptions({
    data: transaccionTipos.transaccionTipos,
    loading: transaccionTipos.isLoading,
    isError: transaccionTipos.isError,
    error: transaccionTipos.error
  });

  const opcionesSistemaPrincipal = useCallback(() => HttpCacheWorkflow.getSistemasPrincipales()
    .then(sis => (
      sis.map(opcion => (
        {
          id: opcion[EntityWithIdFields.Id],
          descripcion: opcion[EntityWithIdAndDescriptionFields.Description],
          icon: <Avatar src={opcion[SistemaPrincipalFields.SrcIcono]} sx={{ height: '20px', width: '20px' }} />
        }
      ))
    )), [])

  const watchedCodClienteTipo = useWatch({ control, name: OperacionADCMFields.CodClienteTipo });
  const watchedCodClienteVirtual = useWatch({ control, name: OperacionADCMFields.CodVirtualCliente });

  useEffect(() => {
    if (watchedCodClienteTipo && clienteTipos.clienteTipos) {
      const seleccionado = clienteTipos.clienteTipos.find((c: any) => c.id === watchedCodClienteTipo);
      const virtualId = seleccionado?.codVirtual;
      setValue(OperacionADCMFields.CodVirtualCliente, virtualId);
      if (virtualId !== ADCMClienteTipo.Cliente) {
        setValue(OperacionADCMFields.NroCuenta, '');
      }
      if (watchedCodClienteVirtual === ADCMClienteTipo.Cliente) {
        formMethods.trigger(OperacionADCMFields.NroCuenta);
      }
    } else {
      setValue(OperacionADCMFields.CodVirtualCliente, null);
    }
  }, [watchedCodClienteTipo, clienteTipos.clienteTipos, setValue]);

  return (
    <CardActions title='Datos Operación' icon='mdi:briefcase-outline' colapsable>
      <Grid container spacing={2.5}>

        {/* Organización */}
        <Grid item xs={12}>
          <SectionBox>
            <Grid item xs={12} md={3}>
              <AsyncSelect
                label='País'
                name={OperacionADCMFields.CodPais}
                loadOptions={opcionesSistemaPrincipal}
                control={control}
                selectNotNulleable
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={12} md={4.5}>
              <AsyncSelect
                label='Unidad de Negocio'
                name={OperacionADCMFields.CodUnidadNegocio}
                valueField={SectorComercialFields.UnidadNegocioCodigo}
                loadOptions={loadUnidadNegocio}
                control={control}
                selectNotNulleable
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4.5}>
              <AsyncSelect
                label='Equipo Comercial'
                name={OperacionADCMFields.CodSectorComercial}
                valueField={SectorComercialFields.SectorComercialCodigo}
                loadOptions={loadSectorComercial}
                control={control}
                selectNotNulleable
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <ControlledDatePicker
                name={OperacionADCMFields.FechaSolicitud}
                control={control}
                disabled
                label='Fecha Solicitud'
              />
            </Grid>
          </SectionBox>
        </Grid>

        {/* Emisor */}
        <Grid item xs={12}>
          <Divider textAlign='left'>
            <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Emisor
            </Typography>
          </Divider>
        </Grid>

        <Grid item xs={12}>
          <SectionBox>
            <Grid item xs={12} md={3}>
              <ControlledTextField
                label='Razón Social'
                name={OperacionADCMFields.RazonSocialEmisor}
                control={control}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <AsyncSelect
                label='Tipo Emisor'
                name={OperacionADCMFields.CodEmisorTipo}
                loadOptions={loadEmisor}
                control={control}
                selectNotNulleable
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <AsyncSelect
                label='Prospecto o Cliente'
                name={OperacionADCMFields.CodClienteTipo}
                loadOptions={loadCliente}
                control={control}
                selectNotNulleable
                fullWidth
              />
            </Grid>
            {watchedCodClienteVirtual === ADCMClienteTipo.Cliente && (
              <Grid item xs={12} md={3}>
                <ControlledTextField
                  label='Nro. Cuenta'
                  name={OperacionADCMFields.NroCuenta}
                  control={control}
                  fullWidth
                />
              </Grid>
            )}
          </SectionBox>
        </Grid>

        {/* Características */}
        <Grid item xs={12}>
          <Divider textAlign='left'>
            <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Características
            </Typography>
          </Divider>
        </Grid>

        <Grid item xs={12}>
          <SectionBox>
            <Grid item xs={12} md={4}>
              <AsyncSelect
                label='Emisor Preaprobado'
                name={OperacionADCMFields.EmisorPreaprobado}
                loadOptions={loadSiNo}
                control={control}
                fullWidth
                selectNotNulleable
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ControlledTextField
                label='Rating Crediticio Emisor'
                name={OperacionADCMFields.RatingCrediticioEmisor}
                control={control}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ControlledTextField
                label='Legislación'
                name={OperacionADCMFields.Legislacion}
                control={control}
                fullWidth
              />
            </Grid>
          </SectionBox>
        </Grid>

        {/* Tipo de operación */}
        <Grid item xs={12}>
          <Divider textAlign='left'>
            <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Tipo de operación
            </Typography>
          </Divider>
        </Grid>

        <Grid item xs={12}>
          <SectionBox>
            <Grid item xs={12} md={4}>
              <AsyncSelect
                label='Tipo Transacción'
                name={OperacionADCMFields.CodTransaccionTipo}
                loadOptions={loadTransaccion}
                control={control}
                selectNotNulleable
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <AsyncSelect
                label='Underwriting'
                name={OperacionADCMFields.Underwriting}
                loadOptions={loadSiNo}
                control={control}
                fullWidth
                selectNotNulleable
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <AsyncSelect
                label='Bridgeloan'
                name={OperacionADCMFields.Bridgeloan}
                loadOptions={loadSiNo}
                control={control}
                fullWidth
                selectNotNulleable
              />
            </Grid>
          </SectionBox>
        </Grid>

      </Grid>
    </CardActions>
  )
}
